import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import { attachVendorOrderRooms } from "../hooks/useVendorOrderRooms";
import { useBranchScope } from "../context/BranchScopeContext";
import { useVendorAccess } from "../hooks/useVendorAccess";
import { vendorOwnerIdOf } from "../utilities/vendorScope";
import socketAuthOptions from "../utilities/socketAuth";
import { useHistory } from "react-router-dom";
import { cardLink, cardLinkProps } from "../utilities/orderDeepLinks";
import { formatAvgDelivery } from "../utilities/deliveryTime";
import { fetchHealthcareVendorOrderStats } from "../actions/healthcareVendorStatsActions";
import {
  fetchHealthcareSections,
  fetchHealthcareVendors,
} from "../actions/healthcareSectionActions";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  makeStyles,
  Button,
  Divider,
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Payment as PaymentIcon,
  LocalShipping as LocalShippingIcon,
  Timer as TimerIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  LocalHospital as LocalHospitalIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  AccessTime as AccessTimeIcon,
  ExitToApp as ExitToAppIcon,
  TrendingUp as TrendingUpIcon,
  Whatshot as WhatshotIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useOrderAlerts from "../hooks/useOrderAlerts";

import newHealthcareOrderAudioNotification from "./../assets/img/restaurant_notification_beep.mp3";
import { getUserFromLocalStorage } from "../utilities/user.js";
import { apiGetVendorShopPath, apiGetVendorAnalytics, apiGetVendorRealtimeAnalytics } from "../api/index";
import ChatAnalytics from "../components/ChatAnalytics.js";
import VendorSalesAnalytics from "../components/VendorSalesAnalytics";
import VendorEarnings from "../components/money/VendorEarnings";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
    backgroundColor: "#f0f2f5",
    minHeight: "100vh",
    overflow: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    position: "relative",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.6), rgba(255,255,255,0.9), rgba(255,255,255,0.6))",
    },
  },
  cardContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(2),
    position: "relative",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  cardFooter: {
    marginTop: "auto",
    padding: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    textAlign: "center",
    backdropFilter: "blur(10px)",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.3px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
  },
  newOrdersFooter: {
    color: "#eeb8b3ff",
    cursor: "pointer",
  },
  pendingDeliveryFooter: {
    color: "#eedcaaff",
  },
  deliveredFooter: {
    color: "#1FA62B",
  },
  assignedOrdersFooter: {
    color: "#e5dac5ff",
  },
  avgDeliveryTimeFooter: {
    color: "#d3d8d8ff",
  },
  paymentMethodsFooter: {
    color: "#fff5daff",
  },
  notificationFooter: {
    color: "#c6d3bbff",
  },
  icon: {
    fontSize: 36,
    opacity: 1,
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
  },
  error: {
    color: theme.palette.error.main,
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  notificationButton: {
    backgroundColor: "#ffffff",
    color: "#4caf50",
    borderRadius: "10px",
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.9rem",
    boxShadow: "0 3px 10px rgba(76, 175, 80, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#4caf50",
      color: "#ffffff",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
    },
  },
  notificationButtonOff: {
    backgroundColor: "#ffffff",
    color: "#f44336",
    borderRadius: "10px",
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.9rem",
    boxShadow: "0 3px 10px rgba(244, 67, 54, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#f44336",
      color: "#ffffff",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 16px rgba(244, 67, 54, 0.4)",
    },
  },
  dashboardTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: "#1a1a1a",
    fontSize: "1.75rem",
    letterSpacing: "-0.5px",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  statNumber: {
    fontWeight: 700,
    textShadow: "0 2px 8px rgba(0,0,0,0.4)",
    marginTop: theme.spacing(0.5),
    color: "#ffffff !important",
  },
  cardTitle: {
    fontWeight: 600,
    letterSpacing: "0.5px",
    fontSize: "0.875rem",
    color: "#ffffff !important",
    textShadow: "0 1px 3px rgba(0,0,0,0.3)",
  },
  paymentMethod: {
    display: "inline-block",
    padding: theme.spacing(0.5, 1.5),
    margin: theme.spacing(0.5),
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#ffffff",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
    backdropFilter: "blur(5px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
  },
}));

const HealthcareVendorDashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  // Every order card is a link to My Orders, opened on the filter the card's
  // count was made with (utilities/orderDeepLinks.js), keyboard included.
  const openOrders = (card, label) => cardLinkProps(() => history.push(cardLink("healthcare", card)), label);
  const { stats, loading, error } = useSelector(
    (state) => state.healthcareVendorStats
  );

  const sections = useSelector(
    (state) => state.healthcareSections.sectionsHealthcare
  );
  const sectionsLoading = useSelector(
    (state) => state.healthcareSections.isLoading
  );
  const healthcareVendors = useSelector(
    (state) => state.healthcareSections.healthcareVendors
  );

  const [healthcareVendorId, setHealthcareVendorId] = useState(null); // Healthcare document ID
  const [ownerVendorId, setOwnerVendorId] = useState(null); // User/Owner ID for order stats
  const [initialLoading, setInitialLoading] = useState(true);

  // ── GA4 Shop Analytics state ─────────────────────────────────────────────
  const [analyticsTab, setAnalyticsTab] = useState('realtime'); // 'realtime' | 'overview'
  const [analyticsPath, setAnalyticsPath] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [realtimeData, setRealtimeData] = useState(null);
  const [realtimeLoading, setRealtimeLoading] = useState(true);
  const [realtimeLastUpdated, setRealtimeLastUpdated] = useState(null);

  // Resolve GA4 path once logged in (backend resolves the Healthcare shop from the auth token)
  useEffect(() => {
    if (!healthcareVendorId) return;
    apiGetVendorShopPath()
      .then((res) => setAnalyticsPath(res.data.path))
      .catch(() => {});
  }, [healthcareVendorId]);

  // Fetch historical analytics once path is known
  useEffect(() => {
    if (!analyticsPath) return;
    setAnalyticsLoading(true);
    apiGetVendorAnalytics(analyticsPath, analyticsDays)
      .then((res) => setAnalyticsData(res.data))
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
  }, [analyticsPath, analyticsDays]);

  // Poll realtime analytics every 2 min — backend resolves shop from auth token
  useEffect(() => {
    if (!healthcareVendorId) return;
    const fetchRealtime = () => {
      setRealtimeLoading(true);
      apiGetVendorRealtimeAnalytics()
        .then((res) => {
          setRealtimeData(res.data);
          setRealtimeLastUpdated(new Date());
        })
        .catch(() => {})
        .finally(() => setRealtimeLoading(false));
    };
    fetchRealtime();
    const interval = setInterval(fetchRealtime, 120000); // 2 min — GA4 quota
    return () => clearInterval(interval);
  }, [healthcareVendorId]);

  const [productStats, setProductStats] = useState({
    totalSections: 0,
    totalProducts: 0,
    totalServices: 0,
    prescriptionItems: 0,
  });

  // Shared, reliable alert engine (audio unlock + hidden-tab desktop notice + Web
  // Push) — replaces this file's bespoke AudioContext + useSound + hasInteracted.
  const alerts = useOrderAlerts({ soundUrl: newHealthcareOrderAudioNotification });
  // The ring itself is AdminNavbar's (one engine on every page, rung by the
  // server's vendor-room order events). This view keeps the toggle.
  // On/Off reflects the ENGINE's persisted state (single source of truth), so the
  // card can't say "On" while the engine is off, and toggling on enables + plays.
  const notificationsEnabled = alerts.enabled;

  const user = getUserFromLocalStorage();
  // The owner's id: a team member's dashboard is the pharmacy's.
  const vendorId = vendorOwnerIdOf(user) || "";
  // Branches (4 Oct 2026): the stats follow the branch picked in the navbar;
  // earnings are head office's.
  const { outletIdParam } = useBranchScope();
  const { isBranchScoped } = useVendorAccess();

  // Set owner vendor ID for order stats
  useEffect(() => {
    if (vendorId) {
      setOwnerVendorId(vendorId);
    }
  }, [vendorId]);

  // Fetch healthcare vendors first
  useEffect(() => {
    if (vendorId) {
      const isSuperAdmin = user?.result?.user_role === "SUPERUSER";
      dispatch(fetchHealthcareVendors(vendorId, isSuperAdmin));
    }
  }, [dispatch, vendorId]);

  // Fetch sections using healthcareId once vendors are loaded
  useEffect(() => {
    if (healthcareVendors.length > 0) {
      const healthcareId = healthcareVendors[0]?.healthcareId;
      if (healthcareId) {
        setHealthcareVendorId(healthcareId);
        dispatch(fetchHealthcareSections(healthcareId));
      }
    }
  }, [dispatch, healthcareVendors]);

  // Calculate product statistics from sections
  useEffect(() => {
    if (sections.length > 0) {
      let products = 0;
      let services = 0;
      let prescriptionItems = 0;

      sections.forEach((section) => {
        section.products?.forEach((product) => {
          if (product.productType === "SERVICE") {
            services++;
          } else {
            products++;
          }
          if (product.requiresPrescription) {
            prescriptionItems++;
          }
        });
      });

      setProductStats({
        totalSections: sections.length,
        totalProducts: products,
        totalServices: services,
        prescriptionItems: prescriptionItems,
      });
    }
  }, [sections]);

  const fetchData = () => {
    if (ownerVendorId) {
      dispatch(fetchHealthcareVendorOrderStats(ownerVendorId, { outletId: outletIdParam })).then(() =>
        setInitialLoading(false)
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch, ownerVendorId]);

  useEffect(() => {
    if (!ownerVendorId) return;

    // Signed in: the room gate refuses an anonymous socket.
    const newSocket = io(process.env.REACT_APP_SERVER_URL, socketAuthOptions());
    // One join for every order room; the old join is the fallback for an
    // older server.
    const stopRooms = attachVendorOrderRooms(newSocket, ownerVendorId, {
      fallback: (s) => s.emit("joinHealthcareVendorRoom", ownerVendorId),
    });

    newSocket.on("healthcareOrderStatusChanged", ({ orderId, status }) => {
      fetchData();
    });

    newSocket.on("newHealthcareOrder", (order) => {
      if (
        order.orderItems.some(
          (item) => item.vendorId.toString() === ownerVendorId
        )
      ) {
        fetchData();
        if (notificationsEnabled) {
          toast.info("New healthcare order received!");
          // The ring is AdminNavbar's now (same server event, every page).
        }
      }
    });

    return () => {
      stopRooms();
      newSocket.off("healthcareOrderStatusChanged");
      newSocket.off("newHealthcareOrder");
      newSocket.disconnect();
    };
  }, [ownerVendorId]);

  // Toggle drives the shared engine (audio unlock + notification permission +
  // Web Push) in addition to this view's own on/off UI state.
  const toggleNotifications = alerts.toggle;

  if (initialLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container className={classes.root}>
      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      )}
      {error && (
        <div>
          <Typography variant="h6" className={classes.error}>
            Error: {error}
          </Typography>
        </div>
      )}
      {!loading && !error && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 10 }}>
            <Typography variant="h4" className={classes.dashboardTitle} style={{ margin: 0 }}>
              <LocalHospitalIcon style={{ fontSize: "1.75rem", color: "#E91E63" }} />
              Healthcare Dashboard
            </Typography>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("openInHouseOrderModal"))}
              style={{ padding: "9px 18px", borderRadius: 8, background: "#f2c811", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", color: "#0f172a", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(242,200,17,0.35)" }}
            >
              + New In-House Order
            </button>
          </div>


          {/* Product & Service Statistics Section */}
          <Typography
            variant="h6"
            style={{ marginBottom: "16px", fontWeight: 600, color: "#555" }}
          >
            Product & Service Catalog
          </Typography>
          <Grid container spacing={2} style={{ marginBottom: "32px" }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={classes.card}
                style={{ backgroundColor: "#E91E63" }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      SECTIONS
                    </Typography>
                    <LocalHospitalIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {productStats.totalSections}
                  </Typography>
                </CardContent>
                <div
                  className={classes.cardFooter}
                  style={{ cursor: "pointer" }}
                  onClick={() => history.push("/admin/healthcare-menu")}
                >
                  <Typography variant="body2" style={{ color: "#ffc6d9" }}>
                    → Product Sections
                  </Typography>
                </div>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={classes.card}
                style={{ backgroundColor: "#4CAF50" }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      PRODUCTS
                    </Typography>
                    <ShoppingCartIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {productStats.totalProducts}
                  </Typography>
                </CardContent>
                <div
                  className={classes.cardFooter}
                  style={{ cursor: "pointer" }}
                  onClick={() => history.push("/admin/healthcare-menu")}
                >
                  <Typography variant="body2" style={{ color: "#b8edb8" }}>
                    → Total Products
                  </Typography>
                </div>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={classes.card}
                style={{ backgroundColor: "#2196F3" }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      SERVICES
                    </Typography>
                    <AssignmentIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {productStats.totalServices}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography variant="body2" style={{ color: "#bbdefb" }}>
                    → Total Services
                  </Typography>
                </div>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                className={classes.card}
                style={{ backgroundColor: "#FF9800" }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      PRESCRIPTION ITEMS
                    </Typography>
                    <CheckCircleIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {productStats.prescriptionItems}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography variant="body2" style={{ color: "#ffe0b2" }}>
                    → Requires Prescription
                  </Typography>
                </div>
              </Card>
            </Grid>
          </Grid>

          {/* ── Sales Analytics (daily / weekly / monthly) ────────────────── */}
          {ownerVendorId && !isBranchScoped && <VendorEarnings vendorId={ownerVendorId} />}
          {ownerVendorId && <VendorSalesAnalytics vendorId={ownerVendorId} />}

          {/* ── Chat Analytics (WhatsApp & app conversations) ─────────────── */}
          {/* Customer chats are head office's (4 Oct 2026): a member limited to
              some branches has no inbox, so no chat figures either. */}
          {!isBranchScoped && <ChatAnalytics vendorId={ownerVendorId} />}

          {/* ── GA4 Shop Analytics (tabbed) ────────────────────────────── */}
          {analyticsPath && (
            <div style={{ marginTop: 24, background: '#0d1117', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ background: 'linear-gradient(135deg,#e3a008,#f59e0b)', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TrendingUpIcon style={{ color: '#141824', fontSize: '1rem' }} />
                  </div>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem' }}>
                      <TrendingUpIcon style={{ color: '#f59e0b', fontSize: '0.9rem', marginRight: 4, verticalAlign: 'middle' }} />
                      Shop Analytics
                    </div>
                    <div style={{ color: '#6b7a99', fontSize: '0.72rem' }}>
                      Google Analytics 4 · lusuku.ug · {analyticsTab === 'realtime' ? 'Live data · polls every 2 min' : `Last ${analyticsDays} days`}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Tab switcher */}
                  <div style={{ display: 'flex', gap: 2, background: '#1a1f2e', borderRadius: 8, padding: 3 }}>
                    <button onClick={() => setAnalyticsTab('overview')} style={{
                      padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.15s',
                      background: analyticsTab === 'overview' ? 'linear-gradient(135deg,#e3a008,#f59e0b)' : 'transparent',
                      color: analyticsTab === 'overview' ? '#141824' : '#6b7a99',
                    }}>
                      <VisibilityIcon style={{ fontSize: '0.8rem', marginRight: 4, verticalAlign: 'middle' }} />Overview
                    </button>
                    <button onClick={() => setAnalyticsTab('realtime')} style={{
                      padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 700, transition: 'all 0.15s',
                      background: analyticsTab === 'realtime' ? 'linear-gradient(135deg,#10b981,#059669)' : 'transparent',
                      color: analyticsTab === 'realtime' ? '#fff' : '#6b7a99',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: analyticsTab === 'realtime' ? '#fff' : '#10b981',
                        animation: 'pulse 1.5s infinite', display: 'inline-block', flexShrink: 0,
                      }} />
                      Realtime
                    </button>
                  </div>
                  {/* Day range — only for overview */}
                  {analyticsTab === 'overview' && (
                    <div style={{ display: 'flex', gap: 2, background: '#1a1f2e', borderRadius: 8, padding: 3 }}>
                      {[7, 14, 30].map((d) => (
                        <button key={d} onClick={() => setAnalyticsDays(d)} style={{
                          padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                          fontSize: '0.72rem', fontWeight: 700, transition: 'all 0.15s',
                          background: analyticsDays === d ? 'linear-gradient(135deg,#e3a008,#f59e0b)' : 'transparent',
                          color: analyticsDays === d ? '#141824' : '#6b7a99',
                        }}>{d}d</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Card body */}
              <div style={{ padding: 20 }}>

                {/* ══════════ OVERVIEW TAB ══════════ */}
                {analyticsTab === 'overview' && (
                  analyticsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <CircularProgress size={20} style={{ color: '#f59e0b' }} />
                      <p style={{ marginTop: 12, color: '#6b7a99', fontSize: '0.82rem' }}>Loading analytics…</p>
                    </div>
                  ) : analyticsData ? (() => {
                    const s = analyticsData.summary;
                    const kpis = [
                      { label: 'Shop Visitors', value: Number(s.totalUsers).toLocaleString(), sub: `Last ${analyticsDays} days`, Icon: PeopleIcon, color: '#e67e22' },
                      { label: 'Page Views', value: Number(s.pageViews).toLocaleString(), sub: `${(Number(s.pageViews)/Math.max(Number(s.sessions),1)).toFixed(1)} per visit`, Icon: VisibilityIcon, color: '#1a56db' },
                      { label: 'Sessions', value: Number(s.sessions).toLocaleString(), sub: 'total visits', Icon: LinkIcon, color: '#0694a2' },
                      { label: 'Avg Time', value: `${Math.floor(Number(s.avgSessionDuration)/60)}m ${Math.round(Number(s.avgSessionDuration)%60)}s`, sub: 'per visit', Icon: AccessTimeIcon, color: '#7e3af2' },
                      { label: 'Bounce Rate', value: `${(Number(s.bounceRate)*100).toFixed(1)}%`, sub: Number(s.bounceRate) < 0.5 ? 'Healthy' : 'High', Icon: ExitToAppIcon, color: Number(s.bounceRate) < 0.5 ? '#057a55' : '#c81e1e' },
                    ];
                    return (
                      <>
                        <Grid container spacing={2} style={{ marginBottom: 16 }}>
                          {kpis.map((k) => (
                            <Grid item xs={6} sm={4} md key={k.label}>
                              <div style={{ background: '#141824', borderRadius: 12, padding: '14px 16px', height: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                  <k.Icon style={{ color: k.color, fontSize: '1rem' }} />
                                  <span style={{ color: '#6b7a99', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 600 }}>{k.label}</span>
                                </div>
                                <div style={{ color: '#fff', fontSize: '1.4rem', fontWeight: 700, lineHeight: 1 }}>{k.value}</div>
                                <div style={{ color: '#4b5563', fontSize: '0.68rem', marginTop: 4 }}>{k.sub}</div>
                              </div>
                            </Grid>
                          ))}
                        </Grid>
                        {(analyticsData.topPages || []).length > 0 && (
                          <div style={{ background: '#141824', borderRadius: 12, padding: '14px 18px' }}>
                            <div style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <WhatshotIcon style={{ color: '#f43f5e', fontSize: '1rem' }} />Top Pages
                            </div>
                            {analyticsData.topPages.slice(0, 5).map((page, i) => {
                              const views = Number(page.screenPageViews);
                              const maxV = Math.max(...analyticsData.topPages.slice(0,5).map(p => Number(p.screenPageViews)), 1);
                              const pct = (views / maxV) * 100;
                              const colors = ['#10b981','#3b82f6','#f59e0b','#8b5cf6','#06b6d4'];
                              const clr = colors[i] || '#10b981';
                              const label = page.pagePath || '/';
                              return (
                                <div key={i} style={{ marginBottom: 10 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: '0.74rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '78%' }} title={label}>{label}</span>
                                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: clr, whiteSpace: 'nowrap', marginLeft: 6 }}>{views.toLocaleString()} views</span>
                                  </div>
                                  <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: clr, borderRadius: 4, transition: 'width 0.8s ease' }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    );
                  })() : (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7a99', fontSize: '0.84rem' }}>
                      No analytics data yet for your shop page.
                    </div>
                  )
                )}

                {/* ══════════ REALTIME TAB ══════════ */}
                {analyticsTab === 'realtime' && (
                  realtimeLoading && !realtimeData ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                      <CircularProgress size={20} style={{ color: '#10b981' }} />
                      <p style={{ marginTop: 12, color: '#6b7a99', fontSize: '0.82rem' }}>Connecting to live data…</p>
                    </div>
                  ) : realtimeData ? (
                    <>
                      {/* Live hero */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{
                            background: 'linear-gradient(135deg,#10b981,#059669)',
                            borderRadius: 16, padding: '18px 28px',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120,
                            boxShadow: '0 4px 20px rgba(16,185,129,0.35)',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />
                              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Active Now</span>
                            </div>
                            <div style={{ fontSize: '2.8rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{realtimeData.activeUsers}</div>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', marginTop: 4 }}>on your shop</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                          {realtimeLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <CircularProgress size={12} style={{ color: '#10b981' }} />
                              <span style={{ color: '#6b7a99', fontSize: '0.7rem' }}>Refreshing…</span>
                            </div>
                          )}
                          {realtimeLastUpdated && (
                            <span style={{ color: '#6b7a99', fontSize: '0.7rem' }}>
                              <AccessTimeIcon style={{ fontSize: '0.75rem', marginRight: 3, verticalAlign: 'middle' }} />
                              Updated {realtimeLastUpdated.toLocaleTimeString()}
                            </span>
                          )}
                          <span style={{ color: '#374151', fontSize: '0.65rem' }}>Polls every 2 min</span>
                        </div>
                      </div>

                      {/* Active pages */}
                      <div style={{ background: '#141824', borderRadius: 12, padding: '16px 18px' }}>
                        <div style={{ color: '#c9d3ec', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'flex', alignItems: 'center', marginBottom: 14, gap: 6 }}>
                          <WhatshotIcon style={{ color: '#f43f5e', fontSize: '1rem' }} />Active Pages
                        </div>
                        {(realtimeData.activePages || []).length === 0 ? (
                          <p style={{ color: '#6b7a99', fontSize: '0.8rem', textAlign: 'center', padding: '20px 0' }}>No visitors on your shop right now.</p>
                        ) : (() => {
                          const pages = realtimeData.activePages;
                          const maxU = Math.max(...pages.map(p => Number(p.activeUsers)), 1);
                          const barColors = ['#10b981','#3b82f6','#f59e0b','#8b5cf6','#06b6d4'];
                          return pages.map((page, i) => {
                            const pct = (Number(page.activeUsers) / maxU) * 100;
                            const clr = barColors[i] || '#10b981';
                            const label = page.unifiedScreenName || '/';
                            return (
                              <div key={i} style={{ marginBottom: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <span style={{ fontSize: '0.74rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '78%' }} title={label}>{label}</span>
                                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: clr, whiteSpace: 'nowrap', marginLeft: 6 }}>
                                    {page.activeUsers} <span style={{ fontWeight: 400, color: '#6b7a99' }}>active</span>
                                  </span>
                                </div>
                                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: clr, borderRadius: 4, transition: 'width 0.8s ease' }} />
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  ) : null
                )}

              </div>
            </div>
          )}

          {/* Order Statistics Section */}
          <Typography
            variant="h6"
            style={{ marginBottom: "16px", fontWeight: 600, color: "#555" }}
          >
            Order Management
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #E91E63 0%, #c31354 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("new", "Open new orders")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      NEW ORDER(S)
                    </Typography>
                    <RefreshIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.newOrders}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.newOrdersFooter}
                  >
                    → Manage Order(s)
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #ED8B16 0%, #d67a0e 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("pending", "Open orders pending delivery")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      PENDING DELIVERY
                    </Typography>
                    <LocalShippingIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.pendingDelivery}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.pendingDeliveryFooter}
                  >
                    → Dispatched
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #004d40 0%, #00332b 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("delivered", "Open delivered orders")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      DELIVERED
                    </Typography>
                    <CheckCircleIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.delivered}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.deliveredFooter}
                  >
                    → Overall Delivered
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #795548 0%, #5d4037 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("assigned", "Open assigned orders")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      ASSIGNED ORDERS
                    </Typography>
                    <AssignmentIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {stats.assigned}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.assignedOrdersFooter}
                  >
                    → Assigned Orders
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #607d8b 0%, #455a64 100%)",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
                {...openOrders("delivered", "Open delivered orders, which the average is made from")}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      AVG DELIVERY TIME
                    </Typography>
                    <TimerIcon className={classes.icon} />
                  </div>
                  <Typography variant="h2" className={classes.statNumber}>
                    {formatAvgDelivery(stats.avgDeliveryMinutes)}
                  </Typography>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.avgDeliveryTimeFooter}
                  >
                    → Average Delivery Time
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #ffc107 0%, #ffa000 100%)",
                  color: "#ffffff",
                }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      PAYMENT METHODS
                    </Typography>
                    <PaymentIcon className={classes.icon} />
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    {/* One chip per tender, each opening the orders paid that way. */}
                    {Object.keys(stats.paymentMethods).map((method) => (
                      <span
                        key={method}
                        className={classes.paymentMethod}
                        style={{ cursor: "pointer" }}
                        {...openOrders({ method }, `Open orders paid by ${method}`)}
                      >
                        {method}: {stats.paymentMethods[method]}
                      </span>
                    ))}
                  </div>
                </CardContent>
                <div
                  className={classes.cardFooter}
                  style={{ cursor: "pointer" }}
                  {...openOrders(null, "Open all orders")}
                >
                  <Typography
                    variant="body2"
                    className={classes.paymentMethodsFooter}
                  >
                    → Payment Methods
                  </Typography>
                </div>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Card
                className={classes.card}
                style={{
                  background:
                    "linear-gradient(135deg, #033B3D 0%, #022528 100%)",
                  color: "#ffffff",
                }}
              >
                <CardContent className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <Typography variant="h6" className={classes.cardTitle}>
                      NOTIFICATION SETTINGS
                    </Typography>
                    {notificationsEnabled ? (
                      <NotificationsIcon className={classes.icon} />
                    ) : (
                      <NotificationsOffIcon className={classes.icon} />
                    )}
                  </div>
                  <Button
                    variant="contained"
                    className={
                      notificationsEnabled
                        ? classes.notificationButton
                        : classes.notificationButtonOff
                    }
                    startIcon={
                      notificationsEnabled ? (
                        <NotificationsIcon />
                      ) : (
                        <NotificationsOffIcon />
                      )
                    }
                    onClick={toggleNotifications}
                  >
                    {notificationsEnabled
                      ? "Notifications On"
                      : "Notifications Off"}
                  </Button>
                </CardContent>
                <div className={classes.cardFooter}>
                  <Typography
                    variant="body2"
                    className={classes.notificationFooter}
                  >
                    → Toggle Notifications
                  </Typography>
                </div>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default HealthcareVendorDashboard;
